import React, { useState, useEffect, useRef, useMemo } from 'react';

/**
 * Generate a color from a name string using hash function
 * @param {string} name - The name to generate color from
 * @returns {string} - Hex color code
 */
const generateColorFromName = (name) => {
  if (!name || typeof name !== 'string') {
    return '#9CA3AF'; // Default gray color
  }

  // Hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate HSL color with fixed saturation and lightness for better visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 65; // Fixed saturation for vibrant colors
  const lightness = 50; // Fixed lightness for good contrast

  // Convert HSL to RGB
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;

  let r, g, b;
  if (h * 6 < 1) {
    r = c;
    g = x;
    b = 0;
  } else if (h * 6 < 2) {
    r = x;
    g = c;
    b = 0;
  } else if (h * 6 < 3) {
    r = 0;
    g = c;
    b = x;
  } else if (h * 6 < 4) {
    r = 0;
    g = x;
    b = c;
  } else if (h * 6 < 5) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Get first letter from children (name)
 * @param {React.ReactNode} children - The children prop
 * @returns {string} - First letter uppercase
 */
const getFirstLetter = (children) => {
  if (!children) return '';
  const text = typeof children === 'string' ? children : String(children);
  return text.trim().charAt(0).toUpperCase();
};

/**
 * Avatar Component
 *
 * @param {object} props
 * @param {string} [props.src] - Image source for the avatar.
 * @param {string} [props.srcSet] - Image source set.
 * @param {React.ReactNode} [props.icon] - Custom icon element to display.
 * @param {string} [props.alt] - Alt text for the image.
 * @param {'large' | 'small' | 'default' | number} [props.size='default'] - Size of the avatar. Can be 'large', 'small', 'default' or a number (px).
 * @param {'circle' | 'square'} [props.shape='circle'] - Shape of the avatar. 'circle' or 'square'.
 * @param {React.ReactNode} props.children - Content of the avatar (usually text/initials or name).
 * @param {string} [props.className] - Custom CSS class.
 * @param {object} [props.style] - Custom styles.
 * @param {function} [props.onClick] - Click handler.
 * @param {function} [props.onError] - Handler for image load failure. return false to prevent default fallback.
 * @returns {React.ReactElement}
 */
const Avatar = ({
  src,
  srcSet,
  icon,
  alt,
  size = 'default',
  shape = 'circle',
  children,
  className = '',
  style = {},
  onClick,
  onError,
  ...rest
}) => {
  const [isImgExist, setIsImgExist] = useState(true);
  const [scale, setScale] = useState(1);
  const avatarChildrenRef = useRef(null);
  const avatarNodeRef = useRef(null);

  // Get first letter from children (name)
  const firstLetter = useMemo(() => getFirstLetter(children), [children]);

  // Generate color from name when no src
  const backgroundColor = useMemo(() => {
    if (src || icon) return null; // Don't generate color if image or icon exists
    if (!children) return '#9CA3AF'; // Default gray if no children
    const name = typeof children === 'string' ? children : String(children);
    return generateColorFromName(name.trim());
  }, [src, icon, children]);

  useEffect(() => {
    setIsImgExist(true);
  }, [src]);

  useEffect(() => {
    if (!avatarChildrenRef.current || !avatarNodeRef.current || src || icon) {
      return;
    }
    const childrenWidth = avatarChildrenRef.current.offsetWidth;
    const nodeWidth = avatarNodeRef.current.offsetWidth;
    if (childrenWidth !== 0 && nodeWidth !== 0) {
      if (size === 'default' || size === 'small' || size === 'large' || typeof size === 'number') {
        const gap = 4;
        if (nodeWidth - gap < childrenWidth) {
          setScale((nodeWidth - gap) / childrenWidth);
        } else {
          setScale(1);
        }
      }
    }
  }, [children, size, src, icon]);

  const handleImgLoadError = () => {
    const errorFlag = onError ? onError() : undefined;
    if (errorFlag !== false) {
      setIsImgExist(false);
    }
  };

  const getSizingClasses = () => {
    if (typeof size === 'number') return '';
    switch (size) {
      case 'large':
        return 'w-10 h-10 text-lg';
      case 'small':
        return 'w-6 h-6 text-sm';
      default:
        return 'w-8 h-8 text-base';
    }
  };

  const getSizingStyle = () => {
    if (typeof size === 'number') {
      return {
        width: size,
        height: size,
        lineHeight: `${size}px`,
        fontSize: size / 2
      };
    }
    return {};
  };

  const roundedClass = shape === 'circle' ? 'rounded-full' : 'rounded-md';

  // Use custom background color if generated, otherwise use default gray
  const bgClass = backgroundColor ? '' : 'bg-gray-300';

  const classes = [
    'inline-flex items-center justify-center overflow-hidden text-white whitespace-nowrap align-middle relative',
    bgClass,
    roundedClass,
    getSizingClasses(),
    className
  ]
    .filter(Boolean)
    .join(' ');

  // Combine background color with other styles
  const combinedStyle = {
    ...getSizingStyle(),
    ...(backgroundColor && { backgroundColor }),
    ...style
  };

  let content;

  if (src && isImgExist) {
    content = (
      <img
        src={src}
        srcSet={srcSet}
        alt={alt}
        className="w-full h-full object-cover"
        onError={handleImgLoadError}
      />
    );
  } else if (icon) {
    content = icon;
  } else {
    // Text content - display first letter when no src
    content = (
      <span
        ref={avatarChildrenRef}
        style={{
          display: 'inline-block',
          transform: `scale(${scale})`,
          transformOrigin: 'center center'
        }}
      >
        {firstLetter}
      </span>
    );
  }

  return (
    <span
      ref={avatarNodeRef}
      className={classes}
      style={combinedStyle}
      onClick={onClick}
      {...rest}
    >
      {content}
    </span>
  );
};

/**
 * Avatar Group Component
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Avatar components to group.
 * @param {string} [props.className] - Custom CSS class.
 * @param {number} [props.maxCount] - Maximum number of avatars to show.
 * @param {object} [props.maxStyle] - Style for the overflow avatar.
 * @param {'large' | 'small' | 'default' | number} [props.size] - Size of avatars in the group.
 * @returns {React.ReactElement}
 */
const Group = ({ children, className = '', maxCount, maxStyle, size, ...rest }) => {
  const childrenArray = React.Children.toArray(children).filter((child) => child);
  const totalCount = childrenArray.length;

  let childrenToShow = childrenArray;

  if (maxCount && maxCount < totalCount) {
    const childrenShow = childrenArray.slice(0, maxCount);
    childrenToShow = [
      ...childrenShow,
      // eslint-disable-next-line react/jsx-key
      <Avatar size={size} style={maxStyle}>
        {`+${totalCount - maxCount}`}
      </Avatar>
    ];
  }

  return (
    <div className={`inline-flex -space-x-3 ${className}`} {...rest}>
      {childrenToShow.map((child, index) => {
        return React.cloneElement(child, {
          key: `avatar-key-${index}`,
          size: child.props.size || size,
          className: `${child.props.className || ''} border-2 border-white dark:border-gray-800`
        });
      })}
    </div>
  );
};

Avatar.Group = Group;

export default Avatar;
